import React, { useState, useCallback, useRef, useEffect } from 'react'
import classNames from 'classnames'
import { motion, AnimatePresence } from 'framer-motion'
import Arrow from './Arrow'
import PropTypes from 'prop-types'

// react-popper not available in UI-only mode; provide very small local fallbacks
const Manager = ({ children }) => <>{children}</>;
const Reference = ({ children }) => children({ ref: () => {} });
const Popper = ({ children, innerRef, style }) => children({ ref: innerRef, style: style || {} });
// simple Portal fallback that just renders children in place
const Portal = ({ children }) => <>{children}</>;

const PopperElement = (props) => {
    const { title, forceUpdate, open } = props

    useEffect(() => {
        if (open) {
            forceUpdate?.()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open])

    return <span>{title}</span>
}

const Tooltip = (props) => {
    const {
        className,
        children,
        title,
        placement,
        wrapperClass,
        isOpen,
        disabled, // 🔒 New disabled prop
        ...rest
    } = props

    const [tooltipOpen, setTooltipOpen] = useState(isOpen)
    const tooltipNode = useRef()

    const tooltipBackground = 'gray-800'
    const tooltipDarkBackground = 'gray-800'

    const defaultTooltipClass = `tooltip bg-${tooltipBackground}`

    const toggleTooltip = useCallback(
        (bool) => {
            if (!isOpen && !disabled) {
                setTooltipOpen(bool)
            }
        },
        [isOpen, disabled]
    )

    return (
        <Manager>
            <Reference>
                {({ ref }) => (
                    <span
                        className={classNames('tooltip-wrapper', wrapperClass)}
                        ref={ref}
                        onMouseEnter={() => toggleTooltip(true)}
                        onMouseLeave={() => toggleTooltip(false)}
                    >
                        {children}
                    </span>
                )}
            </Reference>

            <AnimatePresence>
                {tooltipOpen && !disabled && (
                    <Portal>
                        <Popper
                            placement={placement}
                            innerRef={(node) => (tooltipNode.current = node)}
                            modifiers={[
                                { name: 'arrow', options: { element: Arrow } },
                                { name: 'offset', options: { offset: [0, 7] } },
                            ]}
                            strategy={'fixed'}
                        >
                            {({ ref, style, ...popperProps }) => (
                                <motion.div
                                    className={defaultTooltipClass}
                                    ref={ref}
                                    style={style}
                                    initial={{
                                        opacity: 0,
                                        visibility: 'hidden',
                                    }}
                                    animate={{
                                        opacity: 1,
                                        visibility: 'visible',
                                    }}
                                    exit={{
                                        opacity: 0,
                                        visibility: 'hidden',
                                    }}
                                    transition={{
                                        duration: 0.15,
                                        type: 'tween',
                                    }}
                                >
                                    <PopperElement
                                        open={tooltipOpen}
                                        title={title}
                                        {...rest}
                                        {...popperProps}
                                    />
                                    <Arrow
                                        placement={placement}
                                        color={tooltipBackground}
                                        colorDark={tooltipDarkBackground}
                                    />
                                </motion.div>
                            )}
                        </Popper>
                    </Portal>
                )}
            </AnimatePresence>
        </Manager>
    )
}

Tooltip.propTypes = {
    title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    placement: PropTypes.oneOf([
        'top',
        'top-start',
        'top-end',
        'bottom',
        'bottom-start',
        'bottom-end',
        'right',
        'right-start',
        'right-end',
        'left',
        'left-start',
        'left-end',
    ]),
    wrapperClass: PropTypes.string,
    isOpen: PropTypes.bool,
    disabled: PropTypes.bool,
}

Tooltip.defaultProps = {
    placement: 'top',
    isOpen: false,
    disabled: false,
}

export default Tooltip
